import { useEffect, useState } from "react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { Skeleton } from "./Skeleton.js";
import { fmt, duration } from "../pages/AttendancePage.js";
import { currentAnchor, shiftAnchor, rangeFor } from "../lib/historyRange.js";
import { useMe } from "../context/MeContext.js";
import { HR_ROLES } from "@hrms/types";
import { SpecularButton } from "./ui/specular-button.js";

interface EmployeeSearchResult {
  id: string; firstName: string; lastName: string;
  employeeCode: string; designation: string;
}
interface WeeklyBiometricDay {
  date: string; checkIn: string | null; checkOut: string | null; durationHours: number | null;
}
interface WeeklyBiometricResult {
  weekStart: string; weekEnd: string; mapped: boolean; sourceHasDataForWeek: boolean;
  days: WeeklyBiometricDay[];
}

const card: React.CSSProperties = { background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)" };
const inputStyle: React.CSSProperties = { padding: "7px 12px", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: "13px", width: 260, boxSizing: "border-box" };

function weekdayShort(dateStr: string): string {
  return new Date(dateStr + "T00:00:00Z").toLocaleDateString("en-IN", { weekday: "short", timeZone: "UTC" });
}
function dayNumber(dateStr: string): string {
  return String(new Date(dateStr + "T00:00:00Z").getUTCDate());
}

/**
 * `employee` locks the view to one person — used when embedded in that person's
 * record, where the employee picker would be redundant. Omitted, it behaves as
 * the standalone tab: defaults to the viewer, with an HR-only employee search.
 */
export function WeeklyBiometricAttendance({ employee }: { employee?: EmployeeSearchResult } = {}) {
  const { role } = useMe();
  const isHR = HR_ROLES.has(role);

  const [anchor, setAnchor] = useState(() => currentAnchor("week"));
  const [searchSelected, setSearchSelected] = useState<EmployeeSearchResult | null>(null);
  const selected = employee ?? searchSelected;
  const [searchInput, setSearchInput] = useState("");
  const [searchHits, setSearchHits] = useState<EmployeeSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [data, setData] = useState<WeeklyBiometricResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const range = rangeFor("week", anchor);

  useEffect(() => {
    if (!isHR || !searchInput.trim() || selected) { setSearchHits([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data: res } = await api.get<{ data: EmployeeSearchResult[] }>(`/employees?search=${encodeURIComponent(searchInput.trim())}&limit=8`);
        setSearchHits(res.data);
      } catch (err) { logError("WeeklyBiometricAttendance.search", err); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, selected, isHR]);

  // Keyed on the id, not the object, so an inline `employee` prop can't loop the fetch.
  useEffect(() => {
    void load(selected, anchor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, anchor]);

  async function load(emp: EmployeeSearchResult | null, weekStart: string) {
    setLoading(true); setError("");
    try {
      const path = emp ? `/attendance/${emp.id}/weekly-biometric` : "/attendance/me/weekly-biometric";
      const { data: res } = await api.get<WeeklyBiometricResult>(`${path}?weekStart=${weekStart}`);
      setData(res);
    } catch (err) {
      logError("WeeklyBiometricAttendance.load", err);
      setError(errMsg(err) ?? "Failed to load weekly biometric attendance");
    } finally { setLoading(false); }
  }

  function selectEmployee(hit: EmployeeSearchResult) {
    setSearchSelected(hit); setSearchInput(""); setSearchHits([]);
  }
  function clearEmployee() {
    setSearchSelected(null);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div style={{ ...card, padding: "var(--space-3) var(--space-4)" }}>
        <div style={{ display: "flex", gap: "var(--space-6)", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            <SpecularButton onClick={() => setAnchor(a => shiftAnchor("week", a, -1))}>&larr; Prev Week</SpecularButton>
            <div style={{ fontWeight: 600, fontSize: 14, minWidth: 160, textAlign: "center" }}>{range.label}</div>
            <SpecularButton onClick={() => setAnchor(a => shiftAnchor("week", a, 1))}>Next Week &rarr;</SpecularButton>
          </div>

          {isHR && !employee && (
            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--color-surface-700)", whiteSpace: "nowrap" }}>Employee</label>
              {selected ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", background: "var(--color-info-bg)", border: "1px solid var(--color-info-border)", borderRadius: "var(--radius-md)", fontSize: 13, width: 260, boxSizing: "border-box" }}>
                  <span style={{ color: "var(--color-info-fg)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selected.firstName} {selected.lastName} ({selected.employeeCode})
                  </span>
                  <button onClick={clearEmployee} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--color-info)", fontWeight: 700, lineHeight: 1, padding: "0 2px" }}>×</button>
                </div>
              ) : (
                <input
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search employee to view (default: me)…"
                  style={inputStyle}
                />
              )}
              {!selected && (searchHits.length > 0 || searchLoading) && (
                <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, zIndex: 100, background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", minWidth: 260, maxHeight: 260, overflowY: "auto" }}>
                  {searchLoading && <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--color-surface-500)" }}>Searching…</div>}
                  {!searchLoading && searchHits.map(h => (
                    <div key={h.id} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }} onMouseDown={() => selectEmployee(h)}>
                      <div style={{ fontWeight: 500 }}>{h.firstName} {h.lastName}</div>
                      <div style={{ fontSize: 11, color: "var(--color-surface-500)" }}>{h.employeeCode} · {h.designation}</div>
                    </div>
                  ))}
                  {!searchLoading && searchHits.length === 0 && (
                    <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--color-surface-500)" }}>No matches</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)", overflow: "hidden" }}>
        {error && (
          <div style={{ padding: "10px 14px", background: "var(--color-error-bg-subtle)", borderBottom: "1px solid var(--color-error-border)", fontSize: 13, color: "var(--color-error-fg)" }}>{error}</div>
        )}
        {loading ? (
          <div style={{ padding: "var(--space-4)" }}>
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: i < 6 ? "1px solid var(--color-surface-100)" : "none" }}>
                <Skeleton width={80} height={14} />
                <Skeleton width={70} height={14} />
                <Skeleton width={70} height={14} />
                <Skeleton width={70} height={14} />
              </div>
            ))}
          </div>
        ) : !data || !data.mapped ? (
          <div style={{ padding: "var(--space-8)", textAlign: "center", color: "var(--color-surface-700)" }}>
            Not mapped to a biometric device.
          </div>
        ) : !data.sourceHasDataForWeek ? (
          <div style={{ padding: "var(--space-8)", textAlign: "center", color: "var(--color-surface-700)" }}>
            MDB file not uploaded for this week yet.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                {["Date", "Check In", "Check Out", "Duration"].map(h => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-700)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.days.map((d, i) => (
                <tr key={d.date} style={{ borderBottom: i < data.days.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                  <td style={{ padding: "11px 14px" }}>
                    <span style={{ fontWeight: 600 }}>{dayNumber(d.date)}</span>{" "}
                    <span style={{ fontSize: 12, color: "var(--color-surface-700)", textTransform: "uppercase" }}>{weekdayShort(d.date)}</span>
                  </td>
                  <td style={{ padding: "11px 14px" }}>{fmt(d.checkIn)}</td>
                  <td style={{ padding: "11px 14px" }}>{fmt(d.checkOut)}</td>
                  <td style={{ padding: "11px 14px" }}>{duration(d.checkIn, d.checkOut)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
